import { EventbriteHeader, Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';
import { FavIconLinks } from '@eventbrite/webapp-public-content';
import type { AppProps } from 'next/app';
import NextApp from 'next/app';
import Head from 'next/head';
import { Provider } from 'react-redux';

import {
    AwsRumClientProvider,
    DatadogInit,
    getStatsigEnv,
    useInitAwsRum,
    useInitStatsig,
    type AWSRumProps,
    type StatsigConfiguration,
} from '@/common';
import { CORE_HOSTS } from '@/constants';
import { minimalInitialState } from '@/redux';
import { wrapper } from '@/redux/store';
import type { BaseProps } from '@/types';

import '@/styles/global.scss';
// be_grotesk Founders Grotesk preview override (activated via ?be_grotesk=true)
import '@eventbrite/webapp-fonts/fonts.css';
import { getRequestContext } from '@/utils/requestContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { GlobalFallback } from '@/components/ErrorBoundary/GlobalFallback';
import { RoktInit } from '@/components/RoktInit/RoktInit';
import { getServerSideSplitValues, type SplitValues } from '@/utils/split';
import { createDefaultLocaleInfo } from '@/utils/localeInfo';
import { setup as _setup } from '@eventbrite/legacy-infra.i18n';
import { IntlProvider } from 'react-intl';
import { IncomingMessage } from 'http';

interface HomepageWebappProps extends AppProps, BaseProps {
    awsRumProps: AWSRumProps;
    statsigProps: StatsigConfiguration;
    splitValues: SplitValues;
}

export default function App({
    Component,
    awsRumProps,
    statsigProps,
    pageProps,
    ...rest
}: HomepageWebappProps) {
    const i18nProps = pageProps.i18nProps;

    const awsRum = useInitAwsRum({ ...awsRumProps });
    useInitStatsig({ ...statsigProps });
    const initialState = minimalInitialState({ ...rest, ...pageProps });
    const { store, props } = wrapper.useWrappedStore({ ...rest, pageProps, initialState });

    return (
        <>
            <Head>
                <FavIconLinks stage={props.stage} />
            </Head>
            <DatadogInit stage={props.stage} />
            {pageProps.isHomepageRoute && (
                <RoktInit stage={props.stage} apiKey={pageProps.roktApiKey} />
            )}
            <IntlProvider locale={i18nProps?.locale || 'en'} messages={i18nProps?.messages || {}}>
                <Provider store={store}>
                    <AwsRumClientProvider awsRum={awsRum}>
                        <ErrorBoundary componentName="App" fallback={GlobalFallback}>
                            <Component {...props.pageProps} {...props} />
                        </ErrorBoundary>
                    </AwsRumClientProvider>
                </Provider>
            </IntlProvider>

            <ConsentManagerScripts />
        </>
    );
}

App.getInitialProps = wrapper.getInitialAppProps(() => async (context) => {
    const stage = stageFromString(process.env.STAGE || Stage.TEST);
    const publicUserId = (context.ctx.req?.headers[EventbriteHeader.EB_USER_ID] as string) || '';
    const { tld, serverUrl, locale, host } = getRequestContext(context.ctx.req);
    const localeInfo = createDefaultLocaleInfo(context.ctx.req as IncomingMessage);
    const applicationIds = JSON.parse(process.env.APPLICATION_IDS || '{}') as Record<
        string,
        string
    >;
    const rumGuestRoleArn = process.env.RUM_GUEST_ROLE_ARN;
    const identityPoolId = process.env.IDENTITY_POOL_ID;

    const statsigClientKey = process.env.STATSIG_CLIENT_KEY;
    const statsigConfig: StatsigConfiguration = {
        clientKey: statsigClientKey,
        user: {
            userID: publicUserId,
            locale,
            customIDs: {
                correlationId:
                    (context.ctx.req?.headers[EventbriteHeader.EB_CORRELATION_ID] as string) || '',
                sessionId:
                    (context.ctx.req?.headers[EventbriteHeader.EB_SESSION_ID] as string) || '',
            },
        },
        options: {
            environment: {
                tier: getStatsigEnv(stage),
            },
        },
    };
    const env = {
        serverUrl,
        localeInfo,
        tld,
        ebDomain: host?.replace(/^www\./, '') ?? CORE_HOSTS[stage],
    };

    const splitValues = await getServerSideSplitValues();

    const appProps = await NextApp.getInitialProps(context);
    return {
        ...appProps,
        awsRumProps: {
            applicationIds,
            identityPoolId,
            rumGuestRoleArn,
        },
        statsigProps: statsigConfig,
        publicUserId,
        stage,
        env,
        splitValues,
    };
});
